import dayjs from 'dayjs'
import { values } from 'lodash'
/**
 * Returns the next available sort ID for a collection.
 * @param {Array} col - The collection to check.
 * @returns {number} The next available sort ID.
 */
export const nextSortId = (col) => {
	let _max = 0

	for (const it of values(col)) {
		if (_max < it.sortId) {
			_max = it.sortId
		}
	}

	return _max + 1
}

export const randomInt = (min, max) => {
	return Math.floor(Math.random() * (max - min + 1)) + min
}

export const randomFromArray = (arr) => {
	const idx = Math.floor(Math.random() * arr.length)
	return arr[idx]
}

/**
 * Sets a cookie with the specified name, value, and expiration days.
 *
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to store in the cookie.
 * @param {number} days - The number of days until the cookie expires. If set to 0 or omitted, the cookie will be a session cookie and will expire when the browser is closed.
 *
 * @example
 * setCookie('username', 'JohnDoe', 7); // Sets a cookie that expires in 7 days
 */
export const setCookie = (name, value, days) => {
	let expires = ''
	if (days) {
		const date = new Date()
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
		expires = '; expires=' + date.toUTCString()
	}
	document.cookie = name + '=' + (value || '') + expires + '; path=/'
}

/**
 * Retrieves the value of a cookie by its name.
 *
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string|null} The value of the cookie if found, otherwise null.
 *
 * @example
 * const username = getCookie('username'); // Retrieves the value of the 'username' cookie
 */
export const getCookie = (name) => {
	const value = `; ${document.cookie}`
	const parts = value.split(`; ${name}=`)
	if (parts.length === 2) {
		return parts.pop().split(';').shift()
	}
	return null
}

/**
 * Converts any empty arrays within an object to empty objects.
 * This is useful for ensuring that properties that are expected
 * to be objects (but might initially be represented as empty arrays)
 * are correctly formatted before further processing.
 *
 * @param {Object} obj - The object to process, potentially containing empty arrays.
 * @returns {void} This function modifies the input object in place; it does not return a new object.
 *
 * @example
 * const data = {
 *     roles: [],
 *     categories: { admin: [] },
 *     resources: [],
 * };
 * convertEmptyArrays(data);
 * console.log(data); // { roles: {}, categories: { admin: {} }, resources: {} }
 */
export const convertEmptyArraysToEmptyObject = (obj) => {
	for (const key in obj) {
		if (Array.isArray(obj[key]) && obj[key].length === 0) {
			obj[key] = {}
		} else if (typeof obj[key] === 'object' && obj[key] !== null) {
			convertEmptyArraysToEmptyObject(obj[key])

			// Check for 'start' and 'end' within nested object of categories to convert them into date object
			if (obj[key].start) {
				obj[key].start = dayjs(obj[key].start)
			}
			if (obj[key].end) {
				obj[key].end = dayjs(obj[key].end)
			}
		}
	}
}

export const statesOptions = [
	{ value: '', label: 'State' },
	{ value: 'AL', label: 'Alabama' },
	{ value: 'AK', label: 'Alaska' },
	{ value: 'AZ', label: 'Arizona' },
	{ value: 'AR', label: 'Arkansas' },
	{ value: 'AA', label: 'Armed Forces (AA)' },
	{ value: 'AE', label: 'Armed Forces (AE)' },
	{ value: 'AP', label: 'Armed Forces (AP)' },
	{ value: 'CA', label: 'California' },
	{ value: 'CO', label: 'Colorado' },
	{ value: 'CT', label: 'Connecticut' },
	{ value: 'DE', label: 'Delaware' },
	{ value: 'DC', label: 'District of Columbia' },
	{ value: 'FL', label: 'Florida' },
	{ value: 'GA', label: 'Georgia' },
	{ value: 'HI', label: 'Hawaii' },
	{ value: 'ID', label: 'Idaho' },
	{ value: 'IL', label: 'Illinois' },
	{ value: 'IN', label: 'Indiana' },
	{ value: 'IA', label: 'Iowa' },
	{ value: 'KS', label: 'Kansas' },
	{ value: 'KY', label: 'Kentucky' },
	{ value: 'LA', label: 'Louisiana' },
	{ value: 'ME', label: 'Maine' },
	{ value: 'MD', label: 'Maryland' },
	{ value: 'MA', label: 'Massachusetts' },
	{ value: 'MI', label: 'Michigan' },
	{ value: 'MN', label: 'Minnesota' },
	{ value: 'MS', label: 'Mississippi' },
	{ value: 'MO', label: 'Missouri' },
	{ value: 'MT', label: 'Montana' },
	{ value: 'NE', label: 'Nebraska' },
	{ value: 'NV', label: 'Nevada' },
	{ value: 'NH', label: 'New Hampshire' },
	{ value: 'NJ', label: 'New Jersey' },
	{ value: 'NM', label: 'New Mexico' },
	{ value: 'NY', label: 'New York' },
	{ value: 'NC', label: 'North Carolina' },
	{ value: 'ND', label: 'North Dakota' },
	{ value: 'OH', label: 'Ohio' },
	{ value: 'OK', label: 'Oklahoma' },
	{ value: 'OR', label: 'Oregon' },
	{ value: 'PA', label: 'Pennsylvania' },
	{ value: 'PR', label: 'Puerto Rico' },
	{ value: 'RI', label: 'Rhode Island' },
	{ value: 'SC', label: 'South Carolina' },
	{ value: 'SD', label: 'South Dakota' },
	{ value: 'TN', label: 'Tennessee' },
	{ value: 'TX', label: 'Texas' },
	{ value: 'UT', label: 'Utah' },
	{ value: 'VT', label: 'Vermont' },
	{ value: 'VA', label: 'Virginia' },
	{ value: 'WA', label: 'Washington' },
	{ value: 'WV', label: 'West Virginia' },
	{ value: 'WI', label: 'Wisconsin' },
	{ value: 'WY', label: 'Wyoming' }
]

export const countriesOptions = [
	{ value: 'US', label: 'United States of America' },
	{ value: 'BE', label: 'Belgium' },
	{ value: 'BG', label: 'Bulgaria' },
	{ value: 'CZ', label: 'Czechia' },
	{ value: 'DK', label: 'Denmark' },
	{ value: 'DE', label: 'Germany' },
	{ value: 'GR', label: 'Greece' },
	{ value: 'EE', label: 'Estonia' },
	{ value: 'IE', label: 'Ireland' },
	{ value: 'IT', label: 'Italy' },
	{ value: 'LV', label: 'Latvia' },
	{ value: 'LT', label: 'Lithuania' },
	{ value: 'LU', label: 'Luxembourg' },
	{ value: 'MT', label: 'Malta' },
	{ value: 'NL', label: 'Netherlands' },
	{ value: 'PL', label: 'Poland' },
	{ value: 'PT', label: 'Portugal' },
	{ value: 'RO', label: 'Romania' },
	{ value: 'SK', label: 'Slovakia' },
	{ value: 'SI', label: 'Slovenia' },
	{ value: 'ES', label: 'Spain' },
	{ value: 'FI', label: 'Finland' },
	{ value: 'FR', label: 'France' },
	{ value: 'HR', label: 'Croatia' },
	{ value: 'HU', label: 'Hungary' },
	{ value: 'AT', label: 'Austria' },
	{ value: 'SE', label: 'Sweden' }
]

/**
 * Function that takes a string, and formats it like:
 * "This is a string" -> "This Is A String" (all words capitalized)
 * "This is a String" -> "This is a string" (only the first word capitalized)
 * "this is a string" -> "THIS IS A STRING" (all words capitalized)
 * "THIS is a string" -> "this is a string" (all words lowercased)
 * - show documentation for each type, with examples
 */
export const formatString = (str, type = 'capitalizeFirst') => {
	if (typeof str !== 'string') {
		return str
	}

	switch (type) {
		case 'capitalizeAll':
			return str
				.split(' ')
				.map(
					(word) =>
						word.charAt(0).toUpperCase() +
						word.slice(1).toLowerCase()
				)
				.join(' ')
		case 'capitalizeFirst':
			return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
		case 'uppercaseAll':
			return str.toUpperCase()
		case 'lowercaseAll':
			return str.toLowerCase()
		default:
			return str
	}
}

/**
 * Generates a secure random user token.
 * The token is a 32-character hexadecimal string derived from 16 random bytes.
 * - Used for user identification and authentication purposes. Initially added for Ai WP Chat feature.
 * @returns {string} A secure random user token.
 *
 * @example
 * const userToken = createUserToken();
 * console.log(userToken); // Example output: '9f1c2d3e4b5a6c7d8e9f0a1b2c3d4e5f'
 */
export const createUserToken = () => {
	// 16 random bytes → hex string (32 chars)
	const array = new Uint8Array(16)
	crypto.getRandomValues(array)
	return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('')
}
